import React, { useState, useCallback } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminSimulationsQuery,
  useCreateTestSessionMutation,
  useGetTestSessionsForSimulationQuery,
  AdminSimulationItem,
} from "../../app/services/meseeknaApiV2"
import { MdScience, MdContentCopy, MdCheckCircle } from "react-icons/md"
import { FiSearch, FiClock, FiUser, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { BiTestTube, BiListUl } from "react-icons/bi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUser {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TestIcon = (props: { size?: number; className?: string }): JSX.Element => <MdScience {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CopyIcon = (props: { size?: number; className?: string }): JSX.Element => <MdContentCopy {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckCircleIcon = (props: { size?: number; className?: string }): JSX.Element => <MdCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const XIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronUpIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronUp {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TestTubeIcon = (props: { size?: number; className?: string }): JSX.Element => <BiTestTube {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ListIcon = (props: { size?: number; className?: string }): JSX.Element => <BiListUl {...props} />

const STAGE_OPTIONS = [
  { value: "", label: "All Stages" },
  { value: "development", label: "In Development" },
  { value: "production", label: "In Production" },
]

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-700",
    INACTIVE: "bg-gray-100 text-gray-500",
    DRAFT: "bg-gray-100 text-gray-700",
    IN_PROGRESS: "bg-blue-100 text-blue-700",
    COMPLETED: "bg-amber-100 text-amber-700",
    PUBLISHED: "bg-green-100 text-green-700",
    ARCHIVED: "bg-red-100 text-red-700",
  }
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status.replace("_", " ")}
    </span>
  )
}

const getStageBadge = (stage: "development" | "production") => {
  if (stage === "development") {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">In Development</span>
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">In Production</span>
}

const getDifficultyColor = (difficulty: string) => {
  switch (difficulty.toUpperCase()) {
    case "BEGINNER":
      return "bg-green-100 text-green-800"
    case "INTERMEDIATE":
      return "bg-yellow-100 text-yellow-800"
    case "ADVANCED":
      return "bg-red-100 text-red-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const QuickTestResultsSection: React.FC<{ simulationId: string }> = ({ simulationId }) => {
  const { data, isLoading } = useGetTestSessionsForSimulationQuery(simulationId)
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set())
  const [showAllSessions, setShowAllSessions] = useState(false)

  const toggleRunExpansion = (runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev)
      if (next.has(runId)) {
        next.delete(runId)
      } else {
        next.add(runId)
      }
      return next
    })
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  if (isLoading) {
    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    )
  }

  const sessions = data?.data || []
  const completedRuns = sessions.flatMap((s) => s.completedRuns)

  if (sessions.length === 0) return null

  const displaySessions = showAllSessions ? sessions : sessions.slice(0, 2)

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <div className="flex items-center gap-2 mb-2">
        <TestTubeIcon size={14} className="text-purple-600" />
        <span className="text-xs font-semibold text-gray-900">Test Results</span>
        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
          {completedRuns.length} completed
        </span>
      </div>
      <div className="space-y-2">
        {displaySessions.map((session) => (
          <div key={session.id} className="bg-gray-50 rounded-lg p-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-800">{session.name}</span>
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                session.status === "active" ? "bg-green-100 text-green-700" :
                session.status === "completed" ? "bg-blue-100 text-blue-700" :
                session.status === "expired" ? "bg-gray-100 text-gray-700" :
                "bg-yellow-100 text-yellow-700"
              }`}>
                {session.status}
              </span>
            </div>
            {session.completedRuns.length > 0 ? (
              <div className="space-y-1">
                {session.completedRuns.map((run) => (
                  <div key={run.id} className="bg-white rounded border border-gray-200">
                    <button
                      onClick={() => toggleRunExpansion(run.id)}
                      className="w-full px-2 py-1.5 flex items-center justify-between text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">#{run.runNumber}</span>
                        <span className="text-xs text-gray-700">
                          {run.participantName || run.participantEmail}
                        </span>
                        <span className="text-xs text-gray-400">{formatDuration(run.elapsedSeconds)}</span>
                      </div>
                      {expandedRuns.has(run.id) ? (
                        <ChevronUpIcon size={14} className="text-gray-400" />
                      ) : (
                        <ChevronDownIcon size={14} className="text-gray-400" />
                      )}
                    </button>
                    {expandedRuns.has(run.id) && run.finalResultData && (
                      <div className="px-2 pb-2 border-t border-gray-100">
                        <div className="mt-1 text-xs text-gray-500">Completed: {formatDate(run.completedAt)}</div>
                        <details className="mt-1">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View Result JSON</summary>
                          <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(run.finalResultData, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No completed runs yet</p>
            )}
          </div>
        ))}
      </div>
      {sessions.length > 2 && (
        <button
          onClick={() => setShowAllSessions(!showAllSessions)}
          className="mt-1 text-xs text-blue-600 hover:underline"
        >
          {showAllSessions ? "Show less" : `Show ${sessions.length - 2} more sessions`}
        </button>
      )}
    </div>
  )
}

const QuickTestModal: React.FC<{
  simulation: AdminSimulationItem
  onClose: () => void
}> = ({ simulation, onClose }) => {
  const [sessionName, setSessionName] = useState(`Test: ${simulation.name}`)
  const [testerEmails, setTesterEmails] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [result, setResult] = useState<{
    passcode: string
    shareableLink: string
    expiresAt: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const [createTestSession] = useCreateTestSessionMutation()

  const handleCreate = useCallback(async () => {
    setIsCreating(true)
    setError(null)
    try {
      const emailList = testerEmails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(e => e.length > 0)

      const response = await createTestSession({
        name: sessionName,
        simulationId: simulation.id,
        simulationType: simulation.stage === "production" ? "LEGACY_TEST" : "V2_SIMULATION",
        testerEmails: emailList.length > 0 ? emailList : undefined,
      }).unwrap()

      setResult({
        passcode: response.passcode,
        shareableLink: response.shareableLink,
        expiresAt: response.expiresAt,
      })
    } catch (err: unknown) {
      console.error("Failed to create test session:", err)
      setError((err as { data?: { message?: string } })?.data?.message || "Failed to create test session")
    } finally {
      setIsCreating(false)
    }
  }, [sessionName, simulation.id, simulation.stage, testerEmails, createTestSession])

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }, [])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <TestIcon size={24} className="text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Quick Test Session</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <XIcon size={20} />
          </button>
        </div>
        <div className="px-6 py-4">
          {!result ? (
            <>
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Simulation</p>
                <p className="font-medium text-gray-900">{simulation.name}</p>
                <p className="text-xs text-gray-400 mt-1">{simulation.stage === "development" ? "In Development" : "In Production"}</p>
              </div>
              <div className="mb-4">
                <label htmlFor="sessionName" className="block text-sm font-medium text-gray-700 mb-1">
                  Test Session Name
                </label>
                <input
                  type="text"
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter session name"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="testerEmails" className="block text-sm font-medium text-gray-700 mb-1">
                  Tester Emails (Optional)
                </label>
                <textarea
                  id="testerEmails"
                  value={testerEmails}
                  onChange={(e) => setTesterEmails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter emails separated by commas or newlines"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Add emails of testers who should have access</p>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={isCreating || !sessionName.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? "Creating..." : "Create Test Session"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircleIcon size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Test Session Created!</h3>
                <p className="text-sm text-gray-500 mt-1">Share the link and passcode with your QA team</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Passcode</label>
                <div className="flex gap-2">
                  <div className="flex-1 px-4 py-3 bg-gray-50 rounded-lg font-mono text-lg tracking-widest text-center">
                    {result.passcode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.passcode, "passcode")}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {copiedField === "passcode" ? (
                      <CheckCircleIcon size={20} className="text-green-600" />
                    ) : (
                      <CopyIcon size={20} className="text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Shareable Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={result.shareableLink}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                  />
                  <button
                    onClick={() => copyToClipboard(result.shareableLink, "link")}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {copiedField === "link" ? (
                      <CheckCircleIcon size={20} className="text-green-600" />
                    ) : (
                      <CopyIcon size={20} className="text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  <ClockIcon size={14} className="inline mr-1" />
                  Session expires on {formatDate(result.expiresAt)}
                </p>
              </div>
              <button
                onClick={() => copyToClipboard(
                  `Test Session Link: ${result.shareableLink}\nPasscode: ${result.passcode}`,
                  "all"
                )}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors mb-3"
              >
                {copiedField === "all" ? "Copied!" : "Copy Link & Passcode"}
              </button>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const SimulationCard: React.FC<{
  simulation: AdminSimulationItem
  onQuickTest: (sim: AdminSimulationItem) => void
}> = ({ simulation, onQuickTest }) => {
  const [showTestResults, setShowTestResults] = useState(false)

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 ${!simulation.active ? "opacity-60" : ""}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{simulation.name}</h3>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
            {simulation.description || "No description provided"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {getStageBadge(simulation.stage)}
          {getStatusBadge(simulation.status)}
          {simulation.difficulty && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(simulation.difficulty)}`}>
              {simulation.difficulty}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
        {simulation.creatorName || simulation.creatorEmail ? (
          <div className="flex items-center gap-2 text-gray-600">
            <UserIcon size={14} />
            <span className="truncate">{simulation.creatorName || simulation.creatorEmail}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-400">
            <UserIcon size={14} />
            <span>System</span>
          </div>
        )}
        {simulation.organizationName ? (
          <div className="flex items-center gap-2 text-gray-600">
            <BuildingIcon size={14} />
            <span className="truncate">{simulation.organizationName}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-400">
            <BuildingIcon size={14} />
            <span>Global</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-gray-600">
          <ClockIcon size={14} />
          <span>{simulation.estimatedDuration ? `${simulation.estimatedDuration} min` : "N/A"}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <ListIcon size={14} />
          <span>{simulation.industry || simulation.simType || "General"}</span>
        </div>
      </div>

      <div className="text-xs text-gray-400 mb-3">
        Created: {formatDate(simulation.createdAt)}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onQuickTest(simulation)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          <TestIcon size={16} />
          Quick Test
        </button>
        {simulation.stage === "development" && (
          <button
            onClick={() => setShowTestResults(!showTestResults)}
            className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <TestTubeIcon size={16} />
            {showTestResults ? "Hide" : "Results"}
          </button>
        )}
      </div>

      {showTestResults && simulation.stage === "development" && (
        <QuickTestResultsSection simulationId={simulation.id} />
      )}
    </div>
  )
}

const SimulationsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [stageFilter, setStageFilter] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedSimulation, setSelectedSimulation] = useState<AdminSimulationItem | null>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const queryParams = {
    ...(stageFilter && { stage: stageFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
  }

  const { data, isLoading } = useGetAdminSimulationsQuery(
    Object.keys(queryParams).length > 0 ? queryParams : undefined
  )

  const simulations = data?.data || []

  const handleQuickTest = useCallback((sim: AdminSimulationItem) => {
    setSelectedSimulation(sim)
  }, [])

  const stageCounts = React.useMemo(() => {
    let development = 0
    let production = 0
    simulations.forEach((s) => {
      if (s.stage === "production") production++
      else development++
    })
    return { development, production }
  }, [simulations])

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ListIcon size={24} className="text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">All Simulations</h1>
                  <p className="text-gray-500">
                    Browse all simulations and launch test sessions
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, description, or industry..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <select
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {STAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Summary */}
            {!isLoading && simulations.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="text-sm text-gray-600 flex items-center mr-2">
                  <span className="font-medium">{simulations.length}</span>&nbsp;simulations
                </div>
                <button
                  onClick={() => setStageFilter(stageFilter === "production" ? "" : "production")}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    stageFilter === "production"
                      ? "border-green-400 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  In Production: {stageCounts.production}
                </button>
                <button
                  onClick={() => setStageFilter(stageFilter === "development" ? "" : "development")}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    stageFilter === "development"
                      ? "border-amber-400 bg-amber-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  In Development: {stageCounts.development}
                </button>
              </div>
            )}

            {/* Content */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-9 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : simulations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {simulations.map((sim) => (
                  <SimulationCard
                    key={`${sim.stage}-${sim.id}`}
                    simulation={sim}
                    onQuickTest={handleQuickTest}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <ListIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Simulations Found</h3>
                <p className="text-gray-500">
                  {searchQuery || stageFilter
                    ? "No simulations match your filter criteria."
                    : "There are no simulations in the system yet."}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      {selectedSimulation && (
        <QuickTestModal
          simulation={selectedSimulation}
          onClose={() => setSelectedSimulation(null)}
        />
      )}
    </div>
  )
}

export default SimulationsPage
